import React, {useEffect, useState} from 'react';
import {forEach, includes, isArrayLike, isEmpty, size} from 'lodash';
import classnames from 'classnames';
import CollegeStatusCard from 'shared/college-profile/components/right-rail/college-status-card';
import FixedSimilarCollegesCard from 'shared/college-profile/components/right-rail/fixed-similar-colleges-card';
import CourseCreditsCard from 'shared/college-profile/components/right-rail/course-credits-card';
import CampusesCard from 'shared/college-profile/components/right-rail/campuses-card';
import ScheduleVisitCard from 'shared/college-profile/components/right-rail/schedule-visit-card';
import CollegePopularTags from 'shared/college-profile/components/right-rail/college-popular-tags';
import MobileCollegeStatusCard from 'shared/college-profile/components/right-rail/mobile-college-status-card/mobile-college-status-card';
import usePlatformContext from 'src/hooks/usePlatform';

const RECOMMENDATION_STATUSES = [
  'churned',
  'scholarships_not_yet_available',
  'limited_scholarships',
];

// TODO Tech Debt: Consolidate CollegeProfile and College types that are used all over the place
interface CollegeProfile {
  college: {
    _id: string;
    isPartner: boolean;
    otherCampusListingsIds: string[];
    campusesGroupName: string;
    otherCampuses: $TSFixMe[];
    nickname: string;
    slug: string;
    partnerScholarships: $TSFixMe[];
    studentCollegeTags: $TSFixMe[];
    popularTags: $TSFixMe[];
    isFollowing: boolean;
    maxEarnings: number;
    earningRelationship: {
      statusType: string;
      minGPA: number;
    };
    city: string;
    state: string;
    amount: number;
    partnerApplicationDeadlineDay: string;
    partnerApplicationDeadlineMonth: string;
    partnerApplicationDeadlineYear: string;
    partnerApplicationDeadlineTime: string;
    partnerSubmissionDeadlineDay: string;
    partnerSubmissionDeadlineMonth: string;
    divisor: number;
    followable: boolean;
    name: string;
    logo: string;
    bannerHero: string;
    applicationWebsite: string;
    financialAidWebsite: string;
    admissionsWebsite: string;
    website: string;
    netPriceCalculator: string;
    showClearCostButton: boolean;
  };
  similarPartnerColleges: $TSFixMe[];
  userInfo: {
    user: string;
    academicInterestIds: string[];
    highSchoolInfo: {
      senior: boolean;
    };
    totalCredits: number;
  };
  tab: number;
  loading: boolean;
}

interface Props {
  collegeProfile: CollegeProfile;
  followCollege: () => void;
  unfollowCollege: () => void;
  followSimilarCollege: () => void;
  unfollowSimilarCollege: () => void;
  followOtherCampus: () => void;
  unfollowOtherCampus: () => void;
  getSimilarPartnerColleges: (id: string, eventId: string) => void;
  getOtherCampus: (campusId: string) => void;
  hideSecondaryCards?: boolean;
  recommendationsShownEventId?: string;
  openSubmitEarlyModal: () => void;
}

const RightRail = ({
  collegeProfile,
  followCollege,
  unfollowCollege,
  followSimilarCollege,
  unfollowSimilarCollege,
  followOtherCampus,
  unfollowOtherCampus,
  getSimilarPartnerColleges,
  getOtherCampus,
  hideSecondaryCards,
  recommendationsShownEventId,
  openSubmitEarlyModal,
}: Props) => {
  const [showRecommendations, setShowRecommendations] = useState(false);

  useEffect(() => {
    const {
      college: {_id, earningRelationship, otherCampusListingsIds, otherCampuses},
    } = collegeProfile;

    setShowRecommendations(RECOMMENDATION_STATUSES.includes(earningRelationship.statusType));

    if (showRecommendations) {
      getSimilarPartnerColleges(_id, recommendationsShownEventId || '');
    }

    if (otherCampusListingsIds && otherCampusListingsIds.length !== size(otherCampuses)) {
      forEach(otherCampusListingsIds, (campusId) => {
        getOtherCampus(campusId);
      });
    }
  }, [
    collegeProfile,
    getSimilarPartnerColleges,
    getOtherCampus,
    recommendationsShownEventId,
    showRecommendations,
  ]);

  const showScheduleVisit = () => {
    const {
      college: {slug},
    } = collegeProfile;

    return includes(
      ['florida-international-university', 'gannon-university', 'mcmurry-university'],
      slug
    );
  };

  const {
    college: {
      isPartner,
      otherCampusListingsIds,
      campusesGroupName,
      otherCampuses,
      nickname,
      slug,
      partnerScholarships,
      studentCollegeTags,
      popularTags,
      isFollowing,
      maxEarnings,
    },
    similarPartnerColleges,
    userInfo: {totalCredits},
  } = collegeProfile;

  const roundedTotalCredits = Math.floor(totalCredits);
  const missingCredits = Math.max(15 - roundedTotalCredits, 0);
  const {isIonic} = usePlatformContext();

  return (
    <aside className="college-profile-right-rail" aria-labelledby="college-profile-college-name">
      <CollegeStatusCard
        collegeProfile={collegeProfile}
        followCollege={followCollege}
        unfollowCollege={unfollowCollege}
        missingCredits={missingCredits}
        openSubmitEarlyModal={openSubmitEarlyModal}
        maxEarnings={maxEarnings}
      />
      <div className={classnames({'hide-cards': hideSecondaryCards})}>
        {isIonic && <MobileCollegeStatusCard college={collegeProfile.college} />}
        {showRecommendations && (
          <FixedSimilarCollegesCard
            similarPartnerColleges={similarPartnerColleges}
            followCollege={followSimilarCollege}
            unfollowCollege={unfollowSimilarCollege}
            collegeProfile={collegeProfile}
          />
        )}
        {isPartner && (
          <CourseCreditsCard
            collegeProfile={collegeProfile}
            totalCredits={roundedTotalCredits}
            missingCredits={missingCredits}
          />
        )}
        {showScheduleVisit() && (
          <ScheduleVisitCard
            nickname={nickname}
            slug={slug}
            partnerScholarships={partnerScholarships}
          />
        )}
        {otherCampusListingsIds && (
          <CampusesCard
            otherCampuses={otherCampuses}
            campusesGroupName={campusesGroupName}
            followCollege={followOtherCampus}
            unfollowCollege={unfollowOtherCampus}
          />
        )}
        <CollegePopularTags
          popularTags={popularTags}
          showTagButton={
            isFollowing && isArrayLike(studentCollegeTags) && isEmpty(studentCollegeTags)
          }
          college={collegeProfile.college}
        />
      </div>
    </aside>
  );
};

export default RightRail;
